import { ForumPost } from "protogen/forum_service_pb";
import { ActivityEntityType } from "protogen/common_pb";
import { Box, Typography, IconButton, Divider } from "@mui/material";
import LinkRouter from "../navigation/LinkRouter";
import { UserAvatar } from "../common/CurrentUserAvatar";
import PostLikesAndComments from "./PostLikesAndComments";
import { SCROLL_TO_REF_KEY } from "./types";
import { BADGE_CONFIG } from "../../common/badgeConfig";
import {
  File,
  SquareChevronRight,
  Sparkles,
  LucideIcon,
  MoreVertical,
} from "lucide-react";
import React, { useContext, useState, useRef } from "react";
import { CurrentUserContext } from "../context/RequireAuth";
import { isUserAdmin } from "../../common/userUtils";
import { useDeletePost } from "../../services/forum";
import PopperMenu, { createMenuOption } from "../common/PopperMenu";
import { useNavigate } from "react-router-dom";
import PostReviewContent, { parseReviewMetadata } from "./PostReviewContent";

const IconBox = ({
  Icon,
  color,
}: {
  Icon: LucideIcon;
  color: string | undefined;
}) => (
  <Box
    component="span"
    sx={(theme) => ({
      color:
        color === "teal"
          ? theme.palette.primary.main
          : color || theme.palette.text.secondary,
      display: "inline-flex",
      alignItems: "center",
      verticalAlign: "middle",
    })}
  >
    <Icon size={16} />
  </Box>
);

const NonAnchorLink = ({
  children,
  to,
}: {
  children: React.ReactNode;
  to?: string;
}) => {
  const navigate = useNavigate();
  return (
    <Typography
      variant="bodyHeavy"
      onClick={(e: React.MouseEvent) => {
        if (!to) return;
        e.preventDefault();
        e.stopPropagation();
        navigate(to);
      }}
      sx={{
        textDecoration: "none",
        cursor: "pointer",
        display: "inline",
        padding: "4px 0",
        color: "primary.main",
      }}
    >
      {children}
    </Typography>
  );
};

const LinkedItem = ({
  linkTo,
  displayName,
}: {
  linkTo?: string;
  displayName?: string;
}) => (
  <NonAnchorLink to={linkTo}>
    <Typography
      component="span"
      variant="bodyHeavy"
      sx={(theme) => ({
        color: theme.palette.primary.main,
      })}
    >
      {displayName}
    </Typography>
  </NonAnchorLink>
);

export default ({ post }: { post: ForumPost }) => {
  const currentUser = useContext(CurrentUserContext);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  const authorName = `${post.author?.firstName || ""} ${
    post.author?.lastName || ""
  }`.trim();

  const canRemovePost =
    isUserAdmin(currentUser) || post.author?.ref === currentUser.ref;

  const { request: deletePost } = useDeletePost(() => {
    window.location.reload();
  });

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleRemovePost = () => {
    handleClose();
    deletePost({ ref: post.ref });
  };

  const menuOptions = [createMenuOption("Remove", handleRemovePost, "trash")];

  const metadata = post.body?.payload ? JSON.parse(post.body.payload) : {};
  const review =
    post.entityType === ActivityEntityType.BADGE_EARNED
      ? parseReviewMetadata(metadata)
      : null;

  const getActivityContent = (): {
    content: React.ReactNode;
    subtitle: string | null;
    linkTo?: string | null;
  } => {
    switch (post.entityType) {
      case ActivityEntityType.BADGE_EARNED: {
        const badgeName = metadata.badge_name;
        const badgeConfig = BADGE_CONFIG[badgeName];
        const Icon = badgeConfig.icon;
        const iconColor =
          badgeConfig.color === "DEFAULT" ? undefined : badgeConfig.color;
        return {
          content: (
            <>
              <Typography component="span" variant="body" color="text.primary">
                {authorName} earned a badge
              </Typography>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Typography
                  component="span"
                  variant="bodyHeavy"
                  color="text.primary"
                >
                  {badgeConfig.label}
                </Typography>
                <IconBox Icon={Icon} color={iconColor} />
              </Box>
            </>
          ),
          subtitle: badgeConfig.description,
        };
      }
      case ActivityEntityType.DOCUMENT_SHARED: {
        const documentTitle = post.title || "";
        const documentRef = metadata.document_ref;
        return {
          linkTo: `/documents/${encodeURIComponent(documentRef)}?comments=true`,
          content: (
            <>
              <Typography component="span" variant="body" color="text.primary">
                {authorName} shared a new document
              </Typography>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <IconBox Icon={File} color="teal" />
                <LinkedItem displayName={documentTitle} />
              </Box>
            </>
          ),
          subtitle: null,
        };
      }

      case ActivityEntityType.PLAYBOOK_CREATED: {
        const playbookName = metadata.playbook_name || "";
        const playbookRef = metadata.playbook_ref;
        return {
          linkTo: `/tools/agents/playbooks/${encodeURIComponent(playbookRef)}`,
          content: (
            <>
              <Typography component="span" variant="body" color="text.primary">
                {authorName} created a new playbook
              </Typography>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <IconBox Icon={SquareChevronRight} color="teal" />
                <LinkedItem displayName={playbookName} />
              </Box>
            </>
          ),
          subtitle: null,
        };
      }

      case ActivityEntityType.FAMILY_PROMPT_CREATED: {
        const familyPromptName = metadata.family_prompt_name || "";
        const familyPromptRef = metadata.family_prompt_ref;
        return {
          linkTo: `/prompts?prompt=${encodeURIComponent(familyPromptRef)}`,
          content: (
            <>
              <Typography component="span" variant="body" color="text.primary">
                {authorName} shared a new magic document
              </Typography>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <IconBox Icon={Sparkles} color="teal" />
                <LinkedItem displayName={familyPromptName} />
              </Box>
            </>
          ),
          subtitle: null,
        };
      }

      case ActivityEntityType.ADVISOR_JOINED: {
        const location = metadata.location || "";
        const advisorRef = metadata.advisor_ref;
        const advisorName = metadata.advisor_name;
        return {
          content: (
            <Typography variant="body" color="text.primary">
              <NonAnchorLink to={`/advisor/${encodeURIComponent(advisorRef)}`}>
                <strong>{advisorName}</strong>
              </NonAnchorLink>
              {location ? ` from ${location}` : ""} has joined the Faye
              community. Say hi 👋
            </Typography>
          ),
          subtitle: null,
        };
      }

      default:
        return {
          content: (
            <Typography component="span" variant="body" color="text.primary">
              {post.title}
            </Typography>
          ),
          subtitle: null,
        };
    }
  };

  const { content: activityContent, subtitle, linkTo } = getActivityContent();

  const isCommentable =
    post.entityType !== ActivityEntityType.BADGE_EARNED || !!review;
  const avatarSize =
    post.entityType === ActivityEntityType.BADGE_EARNED ? 32 : 24;
  const activityRow = (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        flexDirection: "row",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <UserAvatar user={post.author!} size={avatarSize} clickable={false} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexWrap: "wrap",
          }}
        >
          {activityContent}
        </Box>
        {subtitle && (
          <Typography variant="bodySmall" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <PostLikesAndComments
          post={post}
          compact
          hideComments={!isCommentable}
          onCommentClick={() => {
            navigate(linkTo || `/community/${encodeURIComponent(post.ref)}`);
          }}
        />
        {canRemovePost && (
          <IconButton
            ref={anchorRef}
            onClick={handleToggle}
            size="small"
            sx={{
              padding: "4px",
              color: "#8E9598",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <MoreVertical size={18} />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  const content = (
    <Box
      sx={(theme) => ({
        display: "flex",
        width: "100%",
        padding: "16px 24px",
        flexDirection: "column",
        gap: "12px",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.border}`,
        background: "#FFF",
        cursor: isCommentable ? "pointer" : "default",
      })}
    >
      {activityRow}
      {review && (
        <>
          <Divider />
          <PostReviewContent review={review} />
        </>
      )}
    </Box>
  );

  return (
    <>
      <LinkRouter
        to={linkTo || `/community/${encodeURIComponent(post.ref)}`}
        onClick={() => {
          sessionStorage.setItem(SCROLL_TO_REF_KEY, post.ref);
        }}
      >
        {content}
      </LinkRouter>
      {canRemovePost && (
        <PopperMenu open={open} anchorRef={anchorRef} handleClose={handleClose}>
          {menuOptions}
        </PopperMenu>
      )}
    </>
  );
};
